/// <amd-module name="Wit/MarkdownEditorReduxAdapter/MarkdownEditorReduxAdapter" />
import { MarkdownComponent } from "MarkdownEditor/MarkdownComponent";
import { IMarkdownComponentProps } from "MarkdownEditor/Components/MarkdownComponent.Props";
import { ConnectedMarkdownPreviewRenderer } from "MarkdownPreviewRenderer";
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { getMarkdownEditorValueByKey } from "Redux/MarkdownEditorSelector";
import { MarkdownActions } from "Wit/MarkdownEditorReduxAdapter/Redux/MarkdownEditorActions";
import { IMarkdownEditorAwareState, IMarkdownEditorKey } from "Wit/MarkdownEditorReduxAdapter/Redux/MarkdownEditorContracts";
import { MarkdownModule } from "Wit/MarkdownEditorReduxAdapter/Redux/MarkdownEditorModule";

export interface IMarkdownEditorReduxAdapterProps extends IMarkdownComponentProps {
    lookupKey: IMarkdownEditorKey;
    initialValue?: string;
}

type MarkdownEditorReduxAdapterPropsType = IMarkdownEditorReduxAdapterProps & typeof Actions;

class MarkdownEditorReduxAdapter extends React.Component<MarkdownEditorReduxAdapterPropsType> {
    public render(): JSX.Element {
        return (
            <DynamicModuleLoader modules={[MarkdownModule]}>
                <MarkdownComponent
                    {...this.props}
                    onTextChanged={this._onTextChange}
                    addAttachments={this._addAttachments}
                    renderPreview={this._renderPreview}
                />
            </DynamicModuleLoader>
        );
    }

    public componentDidMount(): void {
        if (this.props.isActive) {
            const { lookupKey, initialValue } = this.props;
            this.props.initialize(lookupKey, initialValue);
        }
    }

    public componentWillUnmount(): void {
        if (this.props.isActive) {
            this.props.dispose(this.props.lookupKey);
        }
    }

    private _onTextChange = (newText: string) => {
        const { lookupKey, onTextChanged } = this.props;
        onTextChanged(lookupKey, newText);
    };

    private _addAttachments = (files: File[], selectionStart: number, selectionEnd: number) => {
        const { addAttachments, lookupKey, createAttachmentRequested } = this.props;
        // The consumer of MarkdownEditorReduxAdapter should listen to createAttachmentRequested action and handle file uploads.
        // Once upload finishes, consumer should fire createAttachmentSucceeded or createAttachmentFailed action.
        createAttachmentRequested(lookupKey, files, selectionStart, selectionEnd);

        if (addAttachments) {
            addAttachments(files, selectionStart, selectionEnd);
        }
    };

    private _renderPreview = (markdown: string) => {
        const { lookupKey, renderPreview } = this.props;

        if (renderPreview) {
            return renderPreview(markdown);
        } else {
            return Promise.resolve(<ConnectedMarkdownPreviewRenderer lookupKey={lookupKey} markdown={markdown} />);
        }
    };
}

function mapStateToProps(
    state: IMarkdownEditorAwareState,
    providedProps: IMarkdownEditorReduxAdapterProps
): Partial<IMarkdownEditorReduxAdapterProps> {
    const { lookupKey } = providedProps;
    return {
        value: getMarkdownEditorValueByKey(state, lookupKey)
    };
}

const Actions = {
    initialize: MarkdownActions.initializeMarkdown,
    onTextChanged: MarkdownActions.textChanged,
    createAttachmentRequested: MarkdownActions.createAttachmentRequested,
    dispose: MarkdownActions.disposeMarkdown,
    resetPreviewLoading: MarkdownActions.resetPreviewState
};

export const ConnectedMarkdownEditorReduxAdapter = connect(
    mapStateToProps,
    Actions
)(MarkdownEditorReduxAdapter);
